import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { DEFAULT_LANGUAGE_CODE } from "./languageConfig";

// `common` holds truly app-wide UI chrome -- currently just the shared confirm
// dialog (src/components/dialog.tsx), which is mounted once at the app root
// (src/index.tsx) and used by every module, including ones with no namespace of
// their own yet. It must never hold module-specific strings; those belong in that
// module's own namespace.
import commonEn from "./locales/en/common.json";
import commonEs from "./locales/es/common.json";
import commonZh from "./locales/zh/common.json";
import commonRu from "./locales/ru/common.json";
import commonKo from "./locales/ko/common.json";
import commonPtBR from "./locales/pt-BR/common.json";
import commonFr from "./locales/fr/common.json";

import authEn from "./locales/en/auth.json";
import authEs from "./locales/es/auth.json";
import authZh from "./locales/zh/auth.json";
import authRu from "./locales/ru/auth.json";
import authKo from "./locales/ko/auth.json";
import authPtBR from "./locales/pt-BR/auth.json";
import authFr from "./locales/fr/auth.json";

import feedbackEn from "./locales/en/feedback.json";
import feedbackEs from "./locales/es/feedback.json";
import feedbackZh from "./locales/zh/feedback.json";
import feedbackRu from "./locales/ru/feedback.json";
import feedbackKo from "./locales/ko/feedback.json";
import feedbackPtBR from "./locales/pt-BR/feedback.json";
import feedbackFr from "./locales/fr/feedback.json";

import dashboardEn from "./locales/en/dashboard/dashboard.json";
import dashboardEs from "./locales/es/dashboard/dashboard.json";
import dashboardZh from "./locales/zh/dashboard/dashboard.json";
import dashboardRu from "./locales/ru/dashboard/dashboard.json";
import dashboardKo from "./locales/ko/dashboard/dashboard.json";
import dashboardPtBR from "./locales/pt-BR/dashboard/dashboard.json";
import dashboardFr from "./locales/fr/dashboard/dashboard.json";

// Reports keep one JSON per report screen; each language's reports/index.ts barrel
// assembles them into a single `reports` namespace, so adding a report never
// requires touching this file.
import reportsEn from "./locales/en/reports";
import reportsEs from "./locales/es/reports";
import reportsZh from "./locales/zh/reports";
import reportsRu from "./locales/ru/reports";
import reportsKo from "./locales/ko/reports";
import reportsPtBR from "./locales/pt-BR/reports";
import reportsFr from "./locales/fr/reports";

// Sales & Fulfillment keeps one JSON per screen; each language's sales/index.ts
// barrel assembles them into a single `sales` namespace.
import salesEn from "./locales/en/sales";
import salesEs from "./locales/es/sales";
import salesZh from "./locales/zh/sales";
import salesRu from "./locales/ru/sales";
import salesKo from "./locales/ko/sales";
import salesPtBR from "./locales/pt-BR/sales";
import salesFr from "./locales/fr/sales";

// Setup keeps one JSON per screen under subfolders that mirror the key path; each
// language's setting/index.ts barrel assembles them into a single `setting` namespace.
import settingEn from "./locales/en/setting";
import settingEs from "./locales/es/setting";
import settingZh from "./locales/zh/setting";
import settingRu from "./locales/ru/setting";
import settingKo from "./locales/ko/setting";
import settingPtBR from "./locales/pt-BR/setting";
import settingFr from "./locales/fr/setting";

// Inventory keeps one JSON per screen under subfolders that mirror the key path; each
// language's inventory/index.ts barrel assembles them into a single `inventory` namespace.
import inventoryEn from "./locales/en/inventory";
import inventoryEs from "./locales/es/inventory";
import inventoryZh from "./locales/zh/inventory";
import inventoryRu from "./locales/ru/inventory";
import inventoryKo from "./locales/ko/inventory";
import inventoryPtBR from "./locales/pt-BR/inventory";
import inventoryFr from "./locales/fr/inventory";

// Customers keeps one JSON per screen; each language's customers/index.ts barrel
// assembles them into a single `customers` namespace.
import customersEn from "./locales/en/customers";
import customersEs from "./locales/es/customers";
import customersZh from "./locales/zh/customers";
import customersRu from "./locales/ru/customers";
import customersKo from "./locales/ko/customers";
import customersPtBR from "./locales/pt-BR/customers";
import customersFr from "./locales/fr/customers";

// Discounts keeps one JSON per screen; each language's discounts/index.ts barrel
// assembles them into a single `discounts` namespace.
import discountsEn from "./locales/en/discounts";
import discountsEs from "./locales/es/discounts";
import discountsZh from "./locales/zh/discounts";
import discountsRu from "./locales/ru/discounts";
import discountsKo from "./locales/ko/discounts";
import discountsPtBR from "./locales/pt-BR/discounts";
import discountsFr from "./locales/fr/discounts";

// Product Management keeps one JSON per screen; each language's
// productManagement/index.ts barrel assembles them into a single
// `productManagement` namespace.
import productManagementEn from "./locales/en/productManagement";
import productManagementEs from "./locales/es/productManagement";
import productManagementZh from "./locales/zh/productManagement";
import productManagementRu from "./locales/ru/productManagement";
import productManagementKo from "./locales/ko/productManagement";
import productManagementPtBR from "./locales/pt-BR/productManagement";
import productManagementFr from "./locales/fr/productManagement";

import lotteryEn from "./locales/en/lottery.json";
import lotteryEs from "./locales/es/lottery.json";
import lotteryZh from "./locales/zh/lottery.json";
import lotteryRu from "./locales/ru/lottery.json";
import lotteryKo from "./locales/ko/lottery.json";
import lotteryPtBR from "./locales/pt-BR/lottery.json";
import lotteryFr from "./locales/fr/lottery.json";

// Gift Card configuration and Product Import screens each have a single JSON namespace.
import giftCardsEn from "./locales/en/giftCards.json";
import giftCardsEs from "./locales/es/giftCards.json";
import giftCardsZh from "./locales/zh/giftCards.json";
import giftCardsRu from "./locales/ru/giftCards.json";
import giftCardsKo from "./locales/ko/giftCards.json";
import giftCardsPtBR from "./locales/pt-BR/giftCards.json";
import giftCardsFr from "./locales/fr/giftCards.json";

import importProductEn from "./locales/en/importProduct.json";
import importProductEs from "./locales/es/importProduct.json";
import importProductZh from "./locales/zh/importProduct.json";
import importProductRu from "./locales/ru/importProduct.json";
import importProductKo from "./locales/ko/importProduct.json";
import importProductPtBR from "./locales/pt-BR/importProduct.json";
import importProductFr from "./locales/fr/importProduct.json";

// Marketing keeps one JSON per screen; each language's marketing/index.ts
// barrel assembles them into a single `marketing` namespace.
import marketingEn from "./locales/en/marketing";
import marketingEs from "./locales/es/marketing";
import marketingZh from "./locales/zh/marketing";
import marketingRu from "./locales/ru/marketing";
import marketingKo from "./locales/ko/marketing";
import marketingPtBR from "./locales/pt-BR/marketing";
import marketingFr from "./locales/fr/marketing";

// Menu keeps a single flat file mapping backend `menuCode` -> translated
// display name for the LeftPanel sidebar (see src/components/LeftPannel).
import menuEn from "./locales/en/menu.json";
import menuEs from "./locales/es/menu.json";
import menuZh from "./locales/zh/menu.json";
import menuRu from "./locales/ru/menu.json";
import menuKo from "./locales/ko/menu.json";
import menuPtBR from "./locales/pt-BR/menu.json";
import menuFr from "./locales/fr/menu.json";

// Lookup keeps a single flat file mapping backend `lookupId` -> translated
// display value (LOOKUP_TYPE dropdown data, e.g. statuses, discount types).
import lookupEn from "./locales/en/lookup.json";
import lookupEs from "./locales/es/lookup.json";
import lookupZh from "./locales/zh/lookup.json";
import lookupRu from "./locales/ru/lookup.json";
import lookupKo from "./locales/ko/lookup.json";
import lookupPtBR from "./locales/pt-BR/lookup.json";
import lookupFr from "./locales/fr/lookup.json";

// Errors keeps a single flat file for standalone error pages (Unauthorized, 404)
// that aren't part of any feature module.
import errorsEn from "./locales/en/errors.json";
import errorsEs from "./locales/es/errors.json";
import errorsZh from "./locales/zh/errors.json";
import errorsRu from "./locales/ru/errors.json";
import errorsKo from "./locales/ko/errors.json";
import errorsPtBR from "./locales/pt-BR/errors.json";
import errorsFr from "./locales/fr/errors.json";

import { syncDatePickerLocale } from "./datePickerLocale";

const LANGUAGE_STORAGE_KEY = "hubwallet-language";

// Separate from LANGUAGE_STORAGE_KEY, which the detector's cache plugin also writes
// to passively (e.g. a first-ever page load falling back to navigator locale, or this
// device silently inheriting the org default) -- that passive write must NOT count as
// a real preference. This key is set ONLY by an explicit pick from the language
// dropdown (LanguageSwitcher, shared by the header and login page), and is checked
// live (not captured once at module load) so a pick made on the login page, after
// this module has already evaluated, is still honored on that same login.
//
// Priority is always: (1) an explicit dropdown pick on this device, if one was ever
// made -- wins forever, on this device, until the user picks a different language
// from the dropdown themselves; (2) otherwise, the org's configured default language.
// This applies uniformly, including right after someone saves a new org default --
// saving never overrides a device's own explicit dropdown pick, not even the saver's.
const LANGUAGE_EXPLICIT_STORAGE_KEY = "hubwallet-language-explicit";

export function setExplicitLanguage(code: string) {
  try {
    window.localStorage.setItem(LANGUAGE_EXPLICIT_STORAGE_KEY, "1");
  } catch {
    // localStorage unavailable (private mode, etc.) -- language still applies for this session
  }
  i18n.changeLanguage(code);
}

export function hasExplicitLanguagePreference(): boolean {
  try {
    return !!window.localStorage.getItem(LANGUAGE_EXPLICIT_STORAGE_KEY);
  } catch {
    return false;
  }
}

// Applies a default language (from SettingsLanguage/Get, at login, or right after
// SettingsLanguage/Save) to this device, but only when it has no explicit dropdown
// pick of its own -- otherwise this is a no-op, leaving the dropdown pick in place.
export function applyDefaultLanguage(code: string) {
  if (!hasExplicitLanguagePreference()) {
    i18n.changeLanguage(code);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: commonEn, auth: authEn, feedback: feedbackEn, dashboard: dashboardEn, reports: reportsEn, sales: salesEn, setting: settingEn, inventory: inventoryEn, customers: customersEn, discounts: discountsEn, productManagement: productManagementEn, lottery: lotteryEn, giftCards: giftCardsEn, importProduct: importProductEn, marketing: marketingEn, menu: menuEn, lookup: lookupEn, errors: errorsEn },
      es: { common: commonEs, auth: authEs, feedback: feedbackEs, dashboard: dashboardEs, reports: reportsEs, sales: salesEs, setting: settingEs, inventory: inventoryEs, customers: customersEs, discounts: discountsEs, productManagement: productManagementEs, lottery: lotteryEs, giftCards: giftCardsEs, importProduct: importProductEs, marketing: marketingEs, menu: menuEs, lookup: lookupEs, errors: errorsEs },
      zh: { common: commonZh, auth: authZh, feedback: feedbackZh, dashboard: dashboardZh, reports: reportsZh, sales: salesZh, setting: settingZh, inventory: inventoryZh, customers: customersZh, discounts: discountsZh, productManagement: productManagementZh, lottery: lotteryZh, giftCards: giftCardsZh, importProduct: importProductZh, marketing: marketingZh, menu: menuZh, lookup: lookupZh, errors: errorsZh },
      ru: { common: commonRu, auth: authRu, feedback: feedbackRu, dashboard: dashboardRu, reports: reportsRu, sales: salesRu, setting: settingRu, inventory: inventoryRu, customers: customersRu, discounts: discountsRu, productManagement: productManagementRu, lottery: lotteryRu, giftCards: giftCardsRu, importProduct: importProductRu, marketing: marketingRu, menu: menuRu, lookup: lookupRu, errors: errorsRu },
      ko: { common: commonKo, auth: authKo, feedback: feedbackKo, dashboard: dashboardKo, reports: reportsKo, sales: salesKo, setting: settingKo, inventory: inventoryKo, customers: customersKo, discounts: discountsKo, productManagement: productManagementKo, lottery: lotteryKo, giftCards: giftCardsKo, importProduct: importProductKo, marketing: marketingKo, menu: menuKo, lookup: lookupKo, errors: errorsKo },
      "pt-BR": { common: commonPtBR, auth: authPtBR, feedback: feedbackPtBR, dashboard: dashboardPtBR, reports: reportsPtBR, sales: salesPtBR, setting: settingPtBR, inventory: inventoryPtBR, customers: customersPtBR, discounts: discountsPtBR, productManagement: productManagementPtBR, lottery: lotteryPtBR, giftCards: giftCardsPtBR, importProduct: importProductPtBR, marketing: marketingPtBR, menu: menuPtBR, lookup: lookupPtBR, errors: errorsPtBR },
      fr: { common: commonFr, auth: authFr, feedback: feedbackFr, dashboard: dashboardFr, reports: reportsFr, sales: salesFr, setting: settingFr, inventory: inventoryFr, customers: customersFr, discounts: discountsFr, productManagement: productManagementFr, lottery: lotteryFr, giftCards: giftCardsFr, importProduct: importProductFr, marketing: marketingFr, menu: menuFr, lookup: lookupFr, errors: errorsFr },
    },
    fallbackLng: DEFAULT_LANGUAGE_CODE,
    ns: ["common", "auth", "feedback", "dashboard", "reports", "sales", "setting", "inventory", "customers", "discounts", "productManagement", "lottery", "giftCards", "importProduct", "marketing", "menu", "lookup", "errors"],
    defaultNS: "auth",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },
  });

// Keep react-datepicker's calendar chrome (month captions, weekday headers) aligned
// with the active language — it renders from date-fns, not from i18next resources.
syncDatePickerLocale(i18n.resolvedLanguage || i18n.language);
i18n.on("languageChanged", syncDatePickerLocale);

export default i18n;
